import { Card, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import SWR_KEYS from "@/constants/swr-keys";
import useToast from "@/contexts/toast-context";
import { usePlatformId } from "@/hooks/requests/platform/use-platform-id";
import useCreateBankAccount from "@/hooks/requests/t2Pay/use-create-bank-account";
import useGetBankAccounts from "@/hooks/requests/t2Pay/use-get-bank-accounts";
import useRemoveBankAccount from "@/hooks/requests/t2Pay/use-remove-bank-account";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import usePagination from "@/hooks/use-pagination";
import { BankAccount, BankAccountInput } from "@/interfaces/talk2pay.iterfaces";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";
import { PlatformsName } from "@/types/platform.types";

import { BankAccountDialog } from "./components/bankAccountDialog";
import { BankAccountTable } from "./components/bankAccountTable";
import NoBankAccountMessage from "./components/noBankAccount";

type Props = {
  messages: T2PayHomeMessages;
};

function BankAccountCard({ messages }: Props) {
  const { isVisible, handleClose, handleOpen } = useDialog();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const getBankAccounts = useGetBankAccounts({ pageNumber, pageSize });
  const { data, isLoading: isLoadingBankAccounts, error } = getBankAccounts;
  const { remove, isLoading: isLoadingRemove } = useRemoveBankAccount();
  const { create, isLoading: isLoadingCreate } = useCreateBankAccount();
  const { getPlatformByIdentifier } = usePlatformId();
  const platform = getPlatformByIdentifier(PlatformsName.ASAAS);
  const platformId = platform?.id;
  const { showToast } = useToast();
  const { mutate } = useMutate();

  const hasBankAccount = data?.items && data.items.length > 0;

  const onSuccess = () => {
    mutate(SWR_KEYS.getBankAccounts);
    handleClose();
  };

  const onError = () => handleClose();

  const onSubmit = (_formData: BankAccountInput) => {
    if (!platformId) return showToast({ text: messages.generalErrorMessage, type: "error" });

    const successMessage = messages.createBankAccountSuccess;
    const errorMessage = messages.createBankAccountError;

    const formData: BankAccountInput = { ..._formData, platformId };
    create({ formData, successMessage, errorMessage, onSuccess, onError });
  };

  const onRemove = async (bankAccount: BankAccount) => {
    const successMessage = messages.deleteBankAccountSuccess;
    const errorMessage = messages.deleteBankAccountError;
    const _id = Number(bankAccount.id);

    await remove({ _id, onSuccess, errorMessage, successMessage, onError });
  };

  const isLoading = isLoadingRemove || isLoadingCreate || isLoadingBankAccounts;

  return (
    <Card>
      <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h5">{messages.banksAccountsTitleCard}</MDTypography>
      </MDBox>

      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {!isLoading && !hasBankAccount && !error && (
            <NoBankAccountMessage messages={messages} handleOpen={handleOpen} />
          )}

          {isLoading && <Skeleton variant="rectangular" width="100%" height={80} />}

          {error && !isLoading && (
            <AlertBlock
              title={messages.errorTitle}
              description={messages.errorBankAccountDescription}
              alt={messages.errorAlt}
            />
          )}

          {!isLoading && hasBankAccount && !error && data && (
            <BankAccountTable
              messages={messages}
              bankAccounts={data.items}
              handleOpen={handleOpen}
              onRemove={onRemove}
              data={data}
              pagination={pagination}
            />
          )}

          <BankAccountDialog
            isOpen={isVisible}
            onClose={handleClose}
            messages={messages}
            isLoading={false}
            onSubmit={onSubmit}
          />
        </Grid>
      </MDBox>
    </Card>
  );
}

export default BankAccountCard;
